// Kyetech.jsx — ULTRA MODERN USER UI (ADMIN CONTROLLED)

import React, { useEffect, useState, useMemo } from "react";
import { onAuthStateChanged } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
  addDoc,
  serverTimestamp,
  doc,
  getDoc,
  setDoc,
} from "firebase/firestore";
import { auth, authReady, db, mirrorDb } from "../firebase";
import jsPDF from "jspdf";
import { motion, AnimatePresence } from "framer-motion";
import {
  CheckCircle,
  Loader2,
  RefreshCcw,
  Home,
  ClipboardList,
  Headset,
  User,
  ChevronDown,
  LogIn,
  UserPlus,
  Moon,
  Smartphone,
  ChevronRight,
  Gift,
  MessageSquare,
  Clock3,
  ExternalLink,
  Code2,
  ShieldAlert,
  FileCheck,
  XCircle,
  BadgeCheck,
  X,
  Bell,
  Package,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  DEFAULT_PROMO_DISCOUNT_PERCENT,
  formatPromoCountdown,
  getPromoTiming,
  getPromoPrice,
  getPromoSavings,
} from "../utils/promoPricing";

/* ===== NETWORK LOGOS ===== */
import mtnLogo from "../assets/networks/mtn.png";
import airteltigoLogo from "../assets/networks/airteltigo.png";
import telecelLogo from "../assets/networks/telecel.png";

/* ================= CONFIG ================= */
const PAYSTACK_FEE_RATE = 0.02;
const AGENT_REGISTRATION_FEE = 30;

const ADMIN_PHONE =
  import.meta.env?.VITE_ADMIN_PHONE ||
  process.env.REACT_APP_ADMIN_PHONE ||
  "233552483624";

/* ================= NETWORK PREFIXES ================= */
const MTN_PREFIXES = ["024", "054", "055", "059", "025", "053"];
const AIRTELTIGO_PREFIXES = ["026", "027", "056", "057"];

/* ================= NEW — ADMIN COLLECTION HELPER ================= */
const getPackageCollection = (network) => {
  switch (network) {
    case "MTN":
      return "packages_MTN";
    case "AirtelTigo":
      return "packages_AirtelTigo";
    case "Telecel":
      return "packages_Telecel";
    default:
      return "packages_MTN";
  }
};

/* ================= NETWORK STATUS DOT (MOBILE SMALL) ================= */
const NetworkStatusBadge = ({ status }) => {
  const normalized = String(status || "").toLowerCase();
  const map = {
    good: {
      color: "bg-green-500",
      animation: "animate-pulse",
    },
    slow: {
      color: "bg-yellow-400",
      animation: "animate-pulse",
    },
    bad: {
      color: "bg-red-500",
      animation: "animate-ping",
    },
    down: {
      color: "bg-red-500",
      animation: "animate-ping",
    },
  };

  const current = map[normalized] || {
    color: "bg-gray-400",
    animation: "",
  };

  return (
    <span
      title={(normalized === "down" ? "BAD" : normalized.toUpperCase()) || "UNKNOWN"}
      className={`relative inline-flex h-2.5 w-2.5 rounded-full md:h-3 md:w-3 ${current.color} ${current.animation} md:animate-none`}
    />
  );
};


/* ================= STATUS UI ================= */
const StatusBadge = ({ status }) => {
  if (status === "pending") {
    return (
      <div className="flex items-center justify-center gap-2 text-yellow-400 font-bold">
        <Loader2 size={18} className="animate-spin" />
        Pending
      </div>
    );
    }

  if (status === "processing") {
    return (
      <div className="flex items-center justify-center gap-2 text-blue-400 font-bold">
        <RefreshCcw size={18} className="animate-spin" />
        Processing
      </div>
    );
  }

  if (status === "completed") {
    return (
      <motion.div
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ type: "spring", stiffness: 220 }}
        className="flex items-center justify-center gap-2 text-green-400 font-bold"
      >
        <CheckCircle size={18} />
        Completed
      </motion.div>
    );
  }

  return <span className="text-gray-400">{status}</span>;
};

/* ================= COMPONENT ================= */
export default function Kyetech() {
  const [darkMode, setDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem("kyetech-theme");
    return savedTheme === "dark";
  });

  const navigate = useNavigate();

  /* ADMIN DATA */
  const [packages, setPackages] = useState([]);
  const [packagesActive, setPackagesActive] = useState(true);
  const [promoActive, setPromoActive] = useState(false);
  const [promoDiscountPercent, setPromoDiscountPercent] = useState(
    DEFAULT_PROMO_DISCOUNT_PERCENT
  );
  const [promoStartAt, setPromoStartAt] = useState(null);
  const [promoEndAt, setPromoEndAt] = useState(null);
  const [promoNow, setPromoNow] = useState(new Date());

  /* NETWORK FILTER */
  const [selectedNetwork, setSelectedNetwork] = useState("MTN");

  /* USER STATES */
  const [phone, setPhone] = useState("");
  const [transactions, setTransactions] = useState([]);
  const [loadingStatus, setLoadingStatus] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedPkg, setSelectedPkg] = useState(null);
  const [networkStatus, setNetworkStatus] = useState({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successRef, setSuccessRef] = useState("");
  const [activeTab, setActiveTab] = useState("home");
  const [serviceTab, setServiceTab] = useState("data");
  const [showAgentTerms, setShowAgentTerms] = useState(false);
  const [showPackagePicker, setShowPackagePicker] = useState(false);
  const [airtimeAmount, setAirtimeAmount] = useState("");
  const [authUser, setAuthUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [upgradingAgent, setUpgradingAgent] = useState(false);


  /* NETWORK DETECTION */
  const [detectedNetwork, setDetectedNetwork] = useState("");
  const [finalNetwork, setFinalNetwork] = useState("");
  const [isPorted, setIsPorted] = useState(false);

  const PAYSTACK_KEY =
    import.meta.env?.VITE_PAYSTACK_KEY ||
    process.env.REACT_APP_PAYSTACK_KEY || "pk_live_ed1243b2a5430c54b48c055d631c22055e1ebb43";

  
  const apiKey =
    import.meta.env?.VITE_MNOTIFY_KEY ||
    process.env.REACT_APP_MNOTIFY_KEY ||
    "YupEZ0Eo3Uguxwv1FHQZdxJCB";

  const senderId = "Datafie";

  useEffect(() => {
    let mounted = true;

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!mounted) {
        return;
      }

      setAuthUser(user || null);

      if (!user) {
        setUserProfile(null);
        return;
      }

      await authReady;
      const userSnap = await getDoc(doc(db, "users", user.uid));
      if (mounted) {
        setUserProfile(userSnap.exists() ? userSnap.data() : null);
      }
    });

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("kyetech-theme", darkMode ? "dark" : "light");
  }, [darkMode]);

  useEffect(() => {
    const savedSim = localStorage.getItem("kyetech-sim-number") || "";
    if (savedSim && !phone) {
      setPhone(savedSim);
    }
  }, [phone]);

  /* ================= HELPERS ================= */
  const formatGhs = (x) => `GHS ${Number(x || 0).toFixed(2)}`;
  const isValidPhone = (v) => /^0\d{9}$/.test(v);

  const normalizePhone = (p) =>
    p?.startsWith("0") ? "233" + p.slice(1) : p;

  const generateCustomerEmail = (phone) => {
  if (!phone) return "guest@dataarena.store";
  return `${phone}@datafie.store`;
};

  const calculateTotal = (price) => {
    const fee = price * PAYSTACK_FEE_RATE;
    return { totalPaid: price + fee };
  };

  /* ================= GLOBAL SWITCH ================= */
  useEffect(() => {
    async function loadSwitch() {
      const snap = await getDoc(doc(db, "settings", "packages"));
      if (snap.exists()) {
        const packageSettings = snap.data();
        setPackagesActive(packageSettings.packagesActive ?? true);
        setPromoActive(packageSettings.promoActive ?? false);
        setPromoDiscountPercent(
          packageSettings.promoDiscountPercent ?? DEFAULT_PROMO_DISCOUNT_PERCENT
        );
        setPromoStartAt(packageSettings.promoStartAt ?? null);
        setPromoEndAt(packageSettings.promoEndAt ?? null);
        return;
      }

      setPackagesActive(true);
      setPromoActive(false);
      setPromoDiscountPercent(DEFAULT_PROMO_DISCOUNT_PERCENT);
      setPromoStartAt(null);
      setPromoEndAt(null);
    }
    loadSwitch();
  }, []);

  useEffect(() => {
    if (!promoActive) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setPromoNow(new Date());
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [promoActive]);

/* ================= LOAD NETWORK STATUS 🔥 ================= */
  useEffect(() => {
    async function loadNetworkStatus() {
      const snap = await getDoc(doc(db, "network_status", "current"));
      if (snap.exists()) setNetworkStatus(snap.data());
    }
    loadNetworkStatus();
  }, []);

  /* ================= LOAD PACKAGES ================= */
  useEffect(() => {
    async function loadPackages() {
      const colName = getPackageCollection(selectedNetwork);
      const snap = await getDocs(collection(db, colName));
      setPackages(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    }
    loadPackages();
  }, [selectedNetwork]);

  /* ================= NETWORK AUTO-DETECT ================= */
  useEffect(() => {
    if (!isValidPhone(phone)) {
      setDetectedNetwork("");
      setFinalNetwork("");
      setIsPorted(false);
      return;
    }

    const prefix = phone.substring(0, 3);
    let detected = "";

    if (MTN_PREFIXES.includes(prefix)) detected = "MTN";
    if (AIRTELTIGO_PREFIXES.includes(prefix)) detected = "AirtelTigo";
    if (!detected) detected = "Telecel";

    setDetectedNetwork(detected);
    setFinalNetwork(isPorted ? selectedNetwork : detected);
  }, [phone, isPorted, selectedNetwork]);

  /* ================= FILTERED PACKAGES ================= */
  const promoTiming = useMemo(() => {
    return getPromoTiming(
      {
        promoActive,
        promoStartAt,
        promoEndAt,
      },
      promoNow
    );
  }, [promoActive, promoEndAt, promoNow, promoStartAt]);
  const effectivePromoActive = promoTiming.isDiscountActive;

  const filteredPackages = useMemo(() => {
    return packages
      .filter((p) => p.network === selectedNetwork)
      .map((p) => ({
        ...p,
        gb: Number(p.id.replace(/[^0-9]/g, "")),
        promoPrice: getPromoPrice(Number(p.userPrice ?? p.price ?? 0), effectivePromoActive, promoDiscountPercent),
        promoSavings: getPromoSavings(Number(p.userPrice ?? p.price ?? 0), effectivePromoActive, promoDiscountPercent),
      }))
      .filter((p) => p.gb >= 1 && p.gb <= 50)
      .sort((a, b) => a.gb - b.gb);
  }, [effectivePromoActive, packages, promoDiscountPercent, selectedNetwork]);

  const currentOrderNetwork = serviceTab === "airtime" ? selectedNetwork : finalNetwork;
  const currentOrderLabel =
    serviceTab === "airtime"
      ? `Airtime ${formatGhs(Number(airtimeAmount || 0))}`
      : selectedPkg?.id || "";
  const currentOrderPrice =
    serviceTab === "airtime"
      ? Number(airtimeAmount || 0)
      : selectedPkg
        ? getPromoPrice(Number(selectedPkg.userPrice ?? selectedPkg.price ?? 0), effectivePromoActive, promoDiscountPercent)
        : 0;

  const canStartPayment =
    packagesActive &&
    !processing &&
    isValidPhone(phone) &&
    ((serviceTab === "data" && Boolean(selectedPkg)) ||
      (serviceTab === "airtime" && Number(airtimeAmount || 0) > 0));

  const defaultSimNumber = phone || localStorage.getItem("kyetech-sim-number") || "Not set";
  const displayName =
    userProfile?.firstName ||
    userProfile?.fullName?.split(" ")[0] ||
    authUser?.email?.split("@")[0] ||
    "User";
  const alreadyAgent = userProfile?.role === "agent" && userProfile?.paid !== false;

  const setupSimNumber = () => {
    const preset = phone || localStorage.getItem("kyetech-sim-number") || "";
    const entered = window.prompt("Enter your default SIM number (10 digits):", preset);

    if (entered === null) {
      return;
    }

    const clean = entered.trim();
    if (!isValidPhone(clean)) {
      window.alert("SIM number must be 10 digits and start with 0.");
      return;
    }

    localStorage.setItem("kyetech-sim-number", clean);
    setPhone(clean);
    window.alert("SIM setup saved.");
  };

  const startAgentUpgradePayment = async () => {
    if (!authUser) {
      navigate("/agent/login");
      return;
    }

    if (alreadyAgent) {
      window.alert("Your account is already an agent account.");
      return;
    }

    if (!window.PaystackPop || typeof window.PaystackPop.setup !== "function") {
      window.alert("Payment gateway is not available right now.");
      return;
    }

    setUpgradingAgent(true);

    try {
      await new Promise((resolve, reject) => {
        const onUpgradeSuccess = (response) => {
          const reference = response?.reference;
          if (!reference) {
            reject(new Error("Payment completed but no reference was returned."));
            return;
          }

          setDoc(
            doc(db, "users", authUser.uid),
            {
              email: authUser.email || userProfile?.email || "",
              role: "agent",
              approved: true,
              paid: true,
              onboardingFee: AGENT_REGISTRATION_FEE,
              onboardingReference: reference,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          )
            .then(() =>
              setDoc(doc(db, "walletTransactions", `${authUser.uid}_signup_${Date.now()}`), {
                agentId: authUser.uid,
                amount: 0,
                type: "registration",
                note: "Agent onboarding payment completed",
                reference,
                createdAt: serverTimestamp(),
              })
            )
            .then(() => getDoc(doc(db, "users", authUser.uid)))
            .then((updatedSnap) => {
              setUserProfile(updatedSnap.exists() ? updatedSnap.data() : null);
              window.alert("Payment successful. Your account is now an agent account.");
              navigate("/agent/dashboard");
              resolve();
            })
            .catch((error) => {
              reject(error);
            });
        };

        const onUpgradeClose = () => reject(new Error("Payment was cancelled."));

        const handler = window.PaystackPop.setup({
          key: PAYSTACK_KEY,
          email: authUser.email || generateCustomerEmail(phone),
          amount: Math.round(AGENT_REGISTRATION_FEE * 100),
          currency: "GHS",
          metadata: {
            custom_fields: [
              {
                display_name: "Portal",
                variable_name: "portal",
                value: "kyetech_agent_upgrade",
              },
              {
                display_name: "User UID",
                variable_name: "user_uid",
                value: authUser.uid,
              },
            ],
          },
          callback: onUpgradeSuccess,
          onClose: onUpgradeClose,
        });

        if (!handler || typeof handler.openIframe !== "function") {
          reject(new Error("Unable to open payment window."));
          return;
        }

        handler.openIframe();
      });
    } catch (error) {
      window.alert(error?.message || "Unable to complete agent upgrade right now.");
    } finally {
      setUpgradingAgent(false);
    }
  };

  useEffect(() => {
    if (!filteredPackages.length) {
      setSelectedPkg(null);
      return;
    }

    if (!selectedPkg) {
      setSelectedPkg(filteredPackages[0]);
      return;
    }

    const match = filteredPackages.find((pkg) => pkg.id === selectedPkg.id);
    if (!match) {
      setSelectedPkg(filteredPackages[0]);
    }
  }, [filteredPackages, selectedPkg]);

  /* ================= PAYMENT ================= */
  const startPayment = () => {
    const netState = networkStatus?.[selectedNetwork]?.status;

    if (["bad", "down"].includes(String(netState || "").toLowerCase())) {
      alert(`${selectedNetwork} network is currently BAD`);
      return;
    }

    if (netState === "slow") {
      if (!window.confirm(`${selectedNetwork} network is slow. Continue?`))
        return;
    }

    if (!isValidPhone(phone)) return alert("Invalid details");
    if (serviceTab === "data" && !selectedPkg) return alert("Select a data package");
    if (serviceTab === "airtime" && Number(airtimeAmount || 0) <= 0) {
      return alert("Enter a valid airtime amount.");
    }

    setProcessing(true);

    const { totalPaid } = calculateTotal(currentOrderPrice);

   window.PaystackPop.setup({
  key: PAYSTACK_KEY,
  email: generateCustomerEmail(phone),
  amount: Math.round(totalPaid * 100),
  currency: "GHS",

  metadata: {
    custom_fields: [
      {
        display_name: "Phone Number",
        variable_name: "phone",
        value: phone,
      },
      {
        display_name: "Network",
        variable_name: "network",
        value: currentOrderNetwork,
      },
      {
        display_name: "Order",
        variable_name: "order",
        value: currentOrderLabel,
      },
      {
        display_name: "Service",
        variable_name: "service",
        value: serviceTab,
      },
    ],
  },

  callback: (res) => handleSuccess(res.reference),
  onClose: () => setProcessing(false),
}).openIframe();
  };

 const handleSuccess = async (ref) => {
  const orderRef = await addDoc(collection(db, "credentials"), {
    assignedTo: phone,
    network: currentOrderNetwork,
    detectedNetwork,
    isPorted,
    status: "pending",
    type: serviceTab,
    packageId: currentOrderLabel,
    airtimeAmount: serviceTab === "airtime" ? Number(airtimeAmount || 0) : null,
    totalPaid: currentOrderPrice,
    assignedAt: serverTimestamp(),
    paystackRef: ref,
  });

  try {
    await setDoc(doc(mirrorDb, "credentials", orderRef.id), {
      sourceDocId: orderRef.id,
      assignedTo: phone,
      network: currentOrderNetwork,
      detectedNetwork,
      isPorted,
      status: "pending",
      type: serviceTab,
      packageId: currentOrderLabel,
      airtimeAmount: serviceTab === "airtime" ? Number(airtimeAmount || 0) : null,
      totalPaid: currentOrderPrice,
      assignedAt: serverTimestamp(),
      paystackRef: ref,
    });
  } catch {
    // Do not block customer flow if mirror write fails.
  }

  // ✅ Generate PDF Receipt
  generateReceipt(ref);

  // 📩 SEND SMS
  await sendSMS(
    phone,
    `
Your Order on ${phone} received and processing.
Delivery in 5–10 minutes.
Thank you for choosing datafie.site
`
  );

  await sendSMS(
    ADMIN_PHONE,
    `New Order Received
Service: ${serviceTab === "airtime" ? "Airtime" : "Data"}
Item: ${currentOrderLabel}
Amount: ${formatGhs(currentOrderPrice)}
Phone: ${phone}`
  );

  setProcessing(false);
  setSuccessRef(ref);
  setShowSuccessModal(true);
};

  /* ================= BUY ANOTHER ================= */
  const buyAnother = () => {
    setShowSuccessModal(false);
    setSuccessRef("");
    setPhone("");
    setAirtimeAmount("");
    setDetectedNetwork("");
    setFinalNetwork("");
    setIsPorted(false);
  };


  /* ================= STATUS CHECK ================= */
  const checkStatus = async () => {
    if (!isValidPhone(phone)) return alert("Invalid phone number");

    setLoadingStatus(true);
    setTransactions([]);

    const snap = await getDocs(
      query(collection(db, "credentials"), where("assignedTo", "==", phone))
    );

    if (snap.empty) {
      alert("No transactions found");
      setLoadingStatus(false);
      return;
    }

    const localRows = snap.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      assignedAt: d.data().assignedAt,
      date: d.data().assignedAt?.toDate().toLocaleString(),
    }));

    const localIds = localRows.map((row) => row.id).filter(Boolean);
    const sourceIdBatches = [];
    for (let index = 0; index < localIds.length; index += 10) {
      sourceIdBatches.push(localIds.slice(index, index + 10));
    }

    const mirrorSnapshots = await Promise.all(
      sourceIdBatches.map((batch) =>
        getDocs(query(collection(mirrorDb, "credentials"), where("sourceDocId", "in", batch)))
      )
    );

    const mirrorBySourceId = new Map();
    mirrorSnapshots.forEach((snapshot) => {
      snapshot.docs.forEach((item) => {
        const data = item.data();
        if (data.sourceDocId) {
          mirrorBySourceId.set(data.sourceDocId, data);
        }
      });
    });

    const updates = [];
    const mergedRows = localRows.map((row) => {
      const mirrorMatch = mirrorBySourceId.get(row.id);
      const mirrorStatus = String(mirrorMatch?.status || "").toLowerCase();
      const localStatus = String(row.status || "pending").toLowerCase();

      if (mirrorStatus && mirrorStatus !== localStatus) {
        updates.push(
          setDoc(
            doc(db, "credentials", row.id),
            {
              status: mirrorStatus,
              updatedAt: serverTimestamp(),
            },
            { merge: true }
          ).catch(() => undefined)
        );

        return {
          ...row,
          status: mirrorStatus,
        };
      }

      return row;
    });

    if (updates.length) {
      await Promise.all(updates);
    }

    setTransactions(mergedRows);

    setLoadingStatus(false);
  };


  /* ================= SMS ================= */
 async function sendSMS(to, msg) {
  try {
    const p = normalizePhone(to);
    if (!p) return;

    const res = await fetch(
      `https://api.mnotify.com/api/sms/quick?key=${apiKey}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recipient: [p],
          sender: senderId,
          message: msg.trim(),
        }),
      }
    );

    const data = await res.json();

    if (!res.ok) {
      console.error("SMS FAILED:", data);
    } else {
      console.log("SMS SENT:", data);
    }
  } catch (err) {
    console.error("SMS ERROR:", err);
  }
}


  /* ================= RECEIPT ================= */
  function generateReceipt(ref) {
    const pdf = new jsPDF();
    pdf.text("Data fie Receipt", 20, 20);
    pdf.text(`Phone: ${phone}`, 20, 40);
    pdf.text(`Service: ${serviceTab === "airtime" ? "Airtime" : "Data"}`, 20, 50);
    pdf.text(`Item: ${currentOrderLabel}`, 20, 60);
    pdf.text(`Network: ${currentOrderNetwork}`, 20, 70);
    pdf.text(`Amount: ${formatGhs(currentOrderPrice)}`, 20, 80);
    pdf.text(`Ref: ${ref}`, 20, 90);
    pdf.save(`Receipt-${ref}.pdf`);
  }

 return (
   <div className={`min-h-screen px-3 pt-4 pb-24 transition-colors ${darkMode ? "bg-[#0f172a] text-slate-100" : "bg-[#f1f1f4] text-slate-900"}`}>
      <div className="mx-auto w-full max-w-md space-y-4">
        {activeTab === "home" && (
          <>
            <div className="flex items-center justify-between px-1">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-sm font-black uppercase text-white shadow-sm">
                U
              </div>

              <div className="flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-[#274193]">
                  <span className="text-[9px] font-black leading-none text-white">DF</span>
                </div>
                <span className={`text-base font-extrabold tracking-tight ${darkMode ? "text-white" : "text-slate-900"}`}>Data fie</span>
              </div>

              <button className={`rounded-full border p-2 ${darkMode ? "border-slate-700 text-slate-300" : "border-slate-200 text-slate-500"}`} aria-label="Notifications">
                <Bell size={18} />
              </button>
            </div>

            <div className="px-1">
              <p className={`text-xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>Hi, {displayName}</p>
              <p className={`text-sm ${darkMode ? "text-slate-300" : "text-slate-500"}`}>
                Buy affordable data bundles. Click Profile to create account and become agent.
              </p>
            </div>
          </>
        )}

        {(promoTiming.status === "upcoming" || promoTiming.status === "live") && (
          <div className={`rounded-2xl border px-4 py-3 text-sm shadow-sm ${darkMode ? "border-amber-900 bg-amber-950/30" : "border-amber-200 bg-amber-50"}`}>
            <p className={`text-[11px] font-extrabold uppercase tracking-[0.2em] ${darkMode ? "text-amber-300" : "text-amber-700"}`}>Promo Day</p>
            <p className={`mt-1 font-bold ${darkMode ? "text-amber-100" : "text-slate-800"}`}>
              {promoTiming.status === "upcoming" ? "Starts in" : "Time left"} {promoTiming.showCountdown
                ? formatPromoCountdown(promoTiming.remainingMs)
                : "LIVE"}
            </p>
          </div>
        )}

        <div className={`overflow-hidden rounded-[32px] border p-4 shadow-[0_14px_34px_rgba(15,23,42,0.08)] ${darkMode ? "border-slate-700 bg-[linear-gradient(180deg,#0f172a_0%,#111827_100%)]" : "border-[#d8d8df] bg-[linear-gradient(180deg,#fafafc_0%,#f4f5f8_100%)]"}`}>
          {activeTab === "home" && (
            <div className={`${darkMode ? "bg-slate-800" : "bg-[#e5e5ea]"} flex rounded-full p-1`}>
              <button
                onClick={() => setServiceTab("data")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${
                  serviceTab === "data" ? "bg-[#274193] text-white shadow-sm" : darkMode ? "text-slate-300" : "text-slate-500"
                }`}
              >
                Buy Data
              </button>
              <button
                onClick={() => setServiceTab("airtime")}
                className={`flex-1 rounded-full py-2.5 text-sm font-bold transition-all ${
                  serviceTab === "airtime" ? "bg-[#274193] text-white shadow-sm" : darkMode ? "text-slate-300" : "text-slate-500"
                }`}
              >
                Airtime
              </button>
            </div>
          )}

          {activeTab === "home" && (
            <div className="mt-5 space-y-5">
              <div>
                <p className={`mb-2 text-xl font-semibold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>Network</p>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { name: "MTN", label: "MTN", logo: mtnLogo },
                    { name: "Telecel", label: "Telecel", logo: telecelLogo },
                    { name: "AirtelTigo", label: "AT", logo: airteltigoLogo },
                  ].map((net) => (
                    <div key={net.name} className="space-y-1.5">
                      <button
                        onClick={() => setSelectedNetwork(net.name)}
                        className={`flex w-full items-center justify-center gap-2 rounded-full border px-3 py-2.5 text-lg font-bold transition-all ${
                          selectedNetwork === net.name
                            ? net.name === "MTN"
                              ? "border-[#f2c50f] bg-[#fff3b0] text-slate-900 shadow-[0_6px_14px_rgba(242,197,15,0.22)]"
                              : net.name === "Telecel"
                                ? "border-[#ef4444] bg-[#ef4444] text-white shadow-[0_6px_14px_rgba(239,68,68,0.24)]"
                                : "border-[#3b82f6] bg-[#3b82f6] text-white shadow-[0_6px_14px_rgba(59,130,246,0.24)]"
                            : darkMode
                              ? "border-slate-700 bg-slate-800 text-slate-200 hover:border-slate-500"
                              : "border-[#ececf1] bg-white text-slate-600 hover:border-[#c4c8d1]"
                        }`}
                      >
                        <img src={net.logo} alt={net.name} className="h-5 w-5 rounded-full object-cover" />
                        <span>{net.label}</span>
                      </button>
                      {(() => {
                        const rawStatus = String(networkStatus?.[net.name]?.status || "good").toLowerCase();
                        const labelStatus = rawStatus === "down" ? "bad" : rawStatus;
                        const colorClass =
                          labelStatus === "good"
                            ? "text-emerald-500"
                            : labelStatus === "slow"
                              ? "text-amber-500"
                              : labelStatus === "bad"
                                ? "text-red-500"
                                : darkMode
                                  ? "text-slate-300"
                                  : "text-slate-500";

                        const caption = `${labelStatus} network`;

                        return (
                          <div className={`flex items-center justify-center gap-1 text-[11px] font-semibold ${colorClass}`}>
                            <NetworkStatusBadge status={labelStatus} />
                            <span>{caption}</span>
                          </div>
                        );
                      })()}
                    </div>
                  ))}
                </div>
              </div>

              {serviceTab === "data" && (
                <>
                  <div>
                    <label className={`mb-2 block text-xl font-semibold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>Package</label>
                    <button
                      type="button"
                      onClick={() => setShowPackagePicker(true)}
                        className={`relative w-full rounded-2xl border px-4 py-3 text-left text-lg font-semibold outline-none transition-all ${darkMode ? "border-slate-600 bg-slate-950 text-slate-200 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-500 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                    >
                      {selectedPkg ? `${selectedPkg.id} - ${formatGhs(selectedPkg.promoPrice)}` : "Select Package"}
                      <ChevronDown className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
                    </button>
                    {selectedPkg && effectivePromoActive && (
                      <p className="mt-1 text-xs text-emerald-700">
                        Promo price {formatGhs(getPromoPrice(Number(selectedPkg.userPrice ?? selectedPkg.price ?? 0), true, promoDiscountPercent))} from
                        <span className="ml-1 line-through text-slate-400">{formatGhs(Number(selectedPkg.userPrice ?? selectedPkg.price ?? 0))}</span>
                      </p>
                    )}
                  </div>

                  <div>
                    <label className={`mb-2 block text-xl font-semibold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>Beneficiary (Phone Number)</label>
                    <input
                      value={phone}
                      onChange={(event) => setPhone(event.target.value)}
                      maxLength={10}
                      placeholder="024xxxxxxx"
                       className={`w-full rounded-2xl border px-4 py-3 text-lg outline-none transition-all ${darkMode ? "border-slate-600 bg-slate-950 text-slate-100 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20" : "border-slate-300 bg-white text-slate-800 focus:border-[#274193] focus:ring-2 focus:ring-[#274193]/20"}`}
                    />
                  </div>

                  {detectedNetwork && detectedNetwork !== selectedNetwork && (
                    <label className="flex items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800">
                      <input
                        type="checkbox"
                        checked={isPorted}
                        onChange={(event) => setIsPorted(event.target.checked)}
                      />
                      This {detectedNetwork} number is ported to {selectedNetwork}
                    </label>
                  )}
                </>
              )}

              {serviceTab === "airtime" && (
                <div className="flex flex-col items-center justify-center py-16 text-center">
                  <div className={`mb-6 flex h-32 w-32 items-center justify-center rounded-full ${darkMode ? "bg-slate-800" : "bg-slate-200"}`}>
                    <Package size={56} strokeWidth={1.5} className={darkMode ? "text-slate-400" : "text-slate-500"} />
                  </div>

                  <h3 className={`text-xl font-bold ${darkMode ? "text-white" : "text-slate-900"}`}>
                    We are working round the
                  </h3>
                  <p className={`mt-1 max-w-[260px] text-sm ${darkMode ? "text-slate-300" : "text-slate-600"}`}>
                    clock to bring this feature to you.
                  </p>
                </div>
              )}

              <button
                onClick={startPayment}
                disabled={!canStartPayment}
                className={`w-full rounded-full border py-3 text-xl font-extrabold shadow-[3px_4px_0px_#8d8d94] transition ${
                  canStartPayment
                    ? "border-[#274193] bg-[#274193] text-white"
                    : "border-[#8f8f95] bg-[#e5e5ea] text-slate-500"
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {processing ? "Processing..." : serviceTab === "airtime" ? "Buy Airtime" : "Buy Data"}
              </button>
            </div>
          )}

          {activeTab === "purchases" && (
            <div className="mt-4 space-y-3">
              <p className="text-lg font-bold">Purchase History</p>
              <input
                value={phone}
                onChange={(event) => setPhone(event.target.value)}
                maxLength={10}
                placeholder="Enter phone used for purchase"
                className="w-full rounded-2xl border border-[#c4c8d1] bg-transparent px-4 py-3 text-base outline-none"
              />
              <button
                onClick={checkStatus}
                disabled={loadingStatus}
                className="w-full rounded-2xl bg-[#274193] py-3 font-bold text-white"
              >
                {loadingStatus ? "Loading..." : "Load Purchases"}
              </button>

              <div className="space-y-2">
                {(() => {
                  const sortedTxns = [...transactions].sort(
                    (a, b) => (b.assignedAt?.toDate?.() || 0) - (a.assignedAt?.toDate?.() || 0)
                  );
                  const currentPurchase = sortedTxns[0];
                  const pastPurchases = sortedTxns.slice(1);

                  return (
                    <>
                      {currentPurchase && (
                        <>
                          <p className="text-sm font-semibold text-slate-600">Current Purchase</p>
                          <div className="rounded-xl border-2 border-[#274193] bg-blue-50 p-4 text-sm">
                            <p className="font-bold text-slate-900">{currentPurchase.packageId}</p>
                            <p className="mt-1 text-xs font-semibold uppercase tracking-[0.2em] text-[#274193]">{currentPurchase.type || "data"}</p>
                            <p className="text-slate-600">{currentPurchase.assignedTo}</p>
                            <p className="text-slate-500 text-xs">{currentPurchase.date}</p>
                            <div className="mt-3">
                              <StatusBadge status={currentPurchase.status} />
                            </div>
                          </div>
                        </>
                      )}

                      {pastPurchases.length > 0 && (
                        <>
                          <p className="mt-4 text-sm font-semibold text-slate-600">Past Purchases</p>
                          <div className="space-y-2">
                            {pastPurchases.map((txn) => (
                              <div key={txn.id} className="rounded-xl border border-[#d9dbe3] bg-white p-3 text-sm">
                                <p className="font-bold text-slate-700">{txn.packageId}</p>
                                <p className="mt-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#274193]">{txn.type || "data"}</p>
                                <p className="text-slate-500">{txn.assignedTo}</p>
                                <p className="text-slate-400 text-xs">{txn.date}</p>
                                <div className="mt-2">
                                  <StatusBadge status={txn.status} />
                                </div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}

                      {!currentPurchase && !pastPurchases.length && (
                        <p className="text-sm text-slate-500">No purchases loaded yet.</p>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {activeTab === "support" && (
            <div className="mt-1 space-y-4">
              <div className="text-center">
                <p className="text-3xl font-extrabold text-slate-900">How can we help?</p>
                <p className="mt-1 text-sm text-slate-500">Get in touch with our team via WhatsApp</p>
              </div>

              <div className="relative overflow-hidden rounded-3xl bg-[#20ba56] p-5 text-white shadow-[0_10px_24px_rgba(16,185,129,0.28)]">
                <div className="absolute -right-10 -top-8 h-28 w-28 rounded-full bg-white/10" />
                <div className="absolute -right-4 bottom-[-32px] h-28 w-28 rounded-full bg-white/10" />

                <div className="relative flex gap-3">
                  <div className="mt-1 rounded-xl bg-white/20 p-2.5">
                    <MessageSquare size={18} />
                  </div>

                  <div>
                    <p className="text-2xl font-extrabold">Contact Admin</p>
                    <p className="mt-1 max-w-[260px] text-sm text-emerald-50">
                      Chat directly with our admin team for personalized assistance
                    </p>

                    <a
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-white"
                      href={`https://wa.me/${ADMIN_PHONE}?text=Hello%20I%20need%20help%20with%20my%20order`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Start Chat
                      <ExternalLink size={14} />
                    </a>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-[#d7d8de] bg-[#f6f6f8] p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#e8e9ed] p-2.5 text-slate-500">
                    <Clock3 size={18} />
                  </div>

                  <div className="text-slate-600">
                    <p className="text-2xl font-extrabold text-slate-900">Support Hours</p>
                    <p className="mt-1 text-sm leading-relaxed">
                      Our support team is available <span className="font-bold text-slate-900">Monday to Sunday, 7:00 AM - 10:00 PM (GMT)</span>. We aim to respond to all messages within 24 hours.
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-3xl border border-[#d7d8de] bg-[#f6f6f8] p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-xl bg-[#e8e9ed] p-2.5 text-slate-500">
                    <Code2 size={18} />
                  </div>

                  <div className="text-slate-600">
                    <p className="text-2xl font-extrabold text-slate-900">About Us</p>
                    <p className="mt-1 text-sm leading-relaxed">
                      Data fie helps you buy data quickly with trusted support and fast delivery updates.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "profile" && (
            <div className="mt-1 space-y-5">
              <div className="mb-2 flex items-center justify-between">
                <div className="h-10 w-10 rounded-full bg-[radial-gradient(circle_at_top,#fcd34d,#22c55e,#3b82f6)]" />
                <p className={`text-2xl font-extrabold ${darkMode ? "text-white" : "text-slate-900"}`}>Data fie</p>
                <button className={`rounded-full p-2.5 ${darkMode ? "bg-slate-800 text-slate-200" : "bg-[#e7e8ec] text-slate-600"}`} aria-label="Notifications">
                  <Bell size={18} />
                </button>
              </div>

              <div className="text-center">
                <p className={`text-xl font-extrabold ${darkMode ? "text-white" : "text-slate-900"}`}>
                  {authUser ? displayName : "Guest User"}
                </p>
                <p className={`mt-1 text-xs leading-relaxed ${darkMode ? "text-slate-300" : "text-slate-500"}`}>
                  {authUser
                    ? alreadyAgent
                      ? "Your agent account is active. You can continue in the agent dashboard."
                      : `Pay GHS ${AGENT_REGISTRATION_FEE.toFixed(2)} to become an agent and unlock agent tools.`
                    : "Create or sign in to your profile, then pay to become an agent."}
                </p>
              </div>

              <button
                onClick={() => navigate("/agent/login")}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#2f61da] px-4 py-3 text-base font-bold text-white shadow-sm"
              >
                <LogIn size={19} />
                {authUser ? "Manage Account" : "Create Account / Sign In"}
              </button>

              <button
                onClick={() => {
                  if (!authUser) {
                    setShowAgentTerms(true);
                    return;
                  }

                  void startAgentUpgradePayment();
                }}
                disabled={upgradingAgent || alreadyAgent}
                className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#f2be05] px-4 py-3 text-base font-bold text-slate-900 shadow-sm disabled:cursor-not-allowed disabled:opacity-60"
              >
                <UserPlus size={19} />
                {upgradingAgent
                  ? "Processing..."
                  : alreadyAgent
                    ? "Agent Account Active"
                    : `Become An Agent (Pay GHS ${AGENT_REGISTRATION_FEE.toFixed(2)})`}
              </button>

              <div className="rounded-3xl bg-gradient-to-r from-[#2f61da] to-[#2350c9] px-5 py-4 text-white shadow-[0_8px_20px_rgba(47,97,218,0.28)]">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-lg font-extrabold">Invite Friends</p>
                    <p className="mt-1 text-xs text-blue-100">Share the joy!</p>
                  </div>
                  <div className="rounded-2xl bg-white/15 p-2.5">
                    <Gift size={24} />
                  </div>
                </div>
              </div>

              <div className="grid gap-3 sm:grid-cols-2">
                <button
                  type="button"
                  onClick={() => setDarkMode((current) => !current)}
                  className={`rounded-3xl border p-4 text-left transition ${
                    darkMode
                      ? "border-slate-700 bg-slate-800 hover:border-slate-500"
                      : "border-[#d4d7df] bg-[#f8f8fa] hover:border-[#bcc1cc]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className={`rounded-2xl p-2 ${darkMode ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}>
                      <Moon size={18} />
                    </div>
                    <ChevronRight size={18} className="mt-0.5 text-slate-400" />
                  </div>
                  <p className={`mt-3 text-sm font-bold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>Dark Mode</p>
                  <p className={`mt-1 text-xs ${darkMode ? "text-slate-300" : "text-slate-500"}`}>
                    {darkMode ? "Tap to switch to light theme" : "Tap to switch to dark theme"}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={setupSimNumber}
                  className={`rounded-3xl border p-4 text-left transition ${
                    darkMode
                      ? "border-slate-700 bg-slate-800 hover:border-slate-500"
                      : "border-[#d4d7df] bg-[#f8f8fa] hover:border-[#bcc1cc]"
                  }`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className={`rounded-2xl p-2 ${darkMode ? "bg-slate-700 text-slate-100" : "bg-slate-200 text-slate-700"}`}>
                      <Smartphone size={18} />
                    </div>
                    <ChevronRight size={18} className="mt-0.5 text-slate-400" />
                  </div>
                  <p className={`mt-3 text-sm font-bold ${darkMode ? "text-slate-100" : "text-slate-800"}`}>SIM Setup</p>
                  <p className={`mt-1 text-xs ${darkMode ? "text-slate-300" : "text-slate-500"}`}>
                    Default Number: {defaultSimNumber}
                  </p>
                  <p className={`mt-1 text-[11px] ${darkMode ? "text-slate-400" : "text-slate-500"}`}>
                    Keep your most used number ready for faster checkout.
                  </p>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={`fixed bottom-0 left-0 right-0 border-t px-3 py-2 backdrop-blur-xl ${darkMode ? "border-slate-700 bg-slate-900/95" : "border-black/10 bg-white/95"}`}>
        <div className="mx-auto flex w-full max-w-md items-center justify-around rounded-2xl">
          {[
            { id: "home", label: "Home", icon: <Home size={18} /> },
            { id: "purchases", label: "Purchases", icon: <ClipboardList size={18} /> },
            { id: "support", label: "Support", icon: <Headset size={18} /> },
            { id: "profile", label: "Profile", icon: <User size={18} /> },
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`flex flex-col items-center gap-1 rounded-xl px-3 py-2 text-xs font-medium transition-all ${
                activeTab === item.id
                  ? darkMode
                    ? "bg-slate-100 text-slate-900 shadow-md"
                    : "bg-slate-900 text-white shadow-md"
                  : darkMode
                  ? "text-slate-400 hover:text-white"
                  : "text-slate-500 hover:text-slate-900"
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* ===== SUCCESS MODAL ===== */}
      <AnimatePresence>
        {showPackagePicker && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-black/25"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 260 }}
              className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-md rounded-t-3xl border border-[#d8d8de] bg-[#f5f5f7]"
            >
              <div className="flex items-center justify-between border-b border-[#dee0e6] px-4 py-4">
                <h3 className="text-sm font-semibold text-slate-900">Select Package</h3>
                <button
                  type="button"
                  onClick={() => setShowPackagePicker(false)}
                  className="rounded-full p-1 text-slate-500"
                  aria-label="Close package picker"
                >
                  <X size={24} />
                </button>
              </div>

              <div className="max-h-[58vh] overflow-y-auto pb-2">
                {filteredPackages.map((pkg) => {
                  const selected = selectedPkg?.id === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      type="button"
                      onClick={() => {
                        setSelectedPkg(pkg);
                        setShowPackagePicker(false);
                      }}
                      className={`flex w-full items-center justify-between border-b border-[#e3e5ea] px-4 py-2.5 text-left transition ${
                        selected ? "bg-[#e8eef8]" : "bg-[#f5f5f7]"
                      }`}
                    >
                      <div className="flex items-center gap-2.5">
                        <span
                          className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                            selected ? "border-[#2d5fd2] bg-[#2d5fd2] text-white" : "border-[#afb4be]"
                          }`}
                        >
                          {selected && <CheckCircle size={10} />}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">{pkg.id}</span>
                      </div>
                      <span className="text-sm font-semibold text-slate-800">{formatGhs(pkg.promoPrice)}</span>
                    </button>
                  );
                })}
              </div>
            </motion.div>
          </motion.div>
        )}

        {showAgentTerms && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/30 backdrop-blur-[2px]"
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 260 }}
              className="absolute bottom-0 left-0 right-0 mx-auto w-full max-w-md rounded-t-3xl border border-[#d8d8de] bg-[#f5f5f7]"
            >
              <div className="px-4 pb-3 pt-3">
                <div className="mx-auto h-1.5 w-16 rounded-full bg-[#e4e4e9]" />
                <div className="mt-2 flex items-center justify-end">
                  <button
                    onClick={() => setShowAgentTerms(false)}
                    className="rounded-full p-2 text-slate-500"
                    aria-label="Close terms"
                  >
                    <X size={20} />
                  </button>
                </div>

                <div className="max-h-[62vh] space-y-3 overflow-y-auto px-1 pb-2">
                  <p className="text-center text-sm text-slate-500">
                    Please read and accept the following terms and conditions to proceed with your agent registration.
                  </p>

                  <div className="rounded-2xl border border-[#dcdee5] bg-[#f8f8fa] p-3.5">
                    <div className="flex items-start gap-2.5">
                      <BadgeCheck size={18} className="mt-0.5 text-slate-500" />
                      <div>
                        <p className="text-lg font-extrabold text-slate-900">
                          Registration Fee
                          <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-sm text-emerald-700">
                            GHS {AGENT_REGISTRATION_FEE.toFixed(2)}
                          </span>
                        </p>
                        <p className="mt-1 text-sm text-slate-500">
                          A one-time registration fee is required to activate your agent account and access wholesale pricing.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#dcdee5] bg-[#f8f8fa] p-3.5">
                    <div className="flex items-start gap-2.5">
                      <XCircle size={18} className="mt-0.5 text-slate-500" />
                      <div>
                        <p className="text-lg font-extrabold text-slate-900">No Social Media Advertising</p>
                        <p className="mt-1 text-sm text-slate-500">
                          You agree not to advertise platform products or services on social media without prior written approval.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#dcdee5] bg-[#f8f8fa] p-3.5">
                    <div className="flex items-start gap-2.5">
                      <ShieldAlert size={18} className="mt-0.5 text-slate-500" />
                      <div>
                        <p className="text-lg font-extrabold text-slate-900">Account Suspension Policy</p>
                        <p className="mt-1 text-sm text-slate-500">
                          Your account may be suspended or terminated without prior notice if fraudulent activity, policy violations, or suspicious behavior is detected.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#dcdee5] bg-[#f8f8fa] p-3.5">
                    <div className="flex items-start gap-2.5">
                      <FileCheck size={18} className="mt-0.5 text-slate-500" />
                      <div>
                        <p className="text-lg font-extrabold text-slate-900">KYC Verification</p>
                        <p className="mt-1 text-sm text-slate-500">
                          You agree to submit Know Your Customer documents when requested, including valid ID and other verification records.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-[#dcdee5] bg-[#f8f8fa] p-3.5 text-sm text-slate-600">
                    By tapping I Accept, you confirm that you have read, understood, and agree to all the terms and conditions stated above.
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 flex gap-3 border-t border-[#dfdfe5] bg-[#f5f5f7] p-4">
                <button
                  onClick={() => setShowAgentTerms(false)}
                  className="w-1/2 rounded-2xl border border-[#cbccd3] bg-white py-3 text-lg font-bold text-slate-700"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    setShowAgentTerms(false);
                    navigate("/agent/login");
                  }}
                  className="w-1/2 rounded-2xl bg-[#f2be05] py-3 text-lg font-bold text-slate-900"
                >
                  I Accept
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}

        {showSuccessModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.8, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.8, y: 20 }}
              className="w-full max-w-md rounded-2xl border border-green-500/50 bg-gradient-to-br from-white to-slate-100 p-8 ring-1 ring-slate-300"
            >
              {/* SUCCESS ICON */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                className="flex justify-center mb-6"
              >
                <div className="bg-green-500/20 p-4 rounded-full border border-green-400">
                  <CheckCircle size={48} className="text-green-400" />
                </div>
              </motion.div>

              {/* TITLE */}
              <h2 className="text-2xl font-bold text-center text-green-400 mb-2">
                Payment Successful!
              </h2>

              {/* DETAILS */}
              <div className="space-y-4 mb-8 text-center">
                <p className="text-slate-700">
                  Your order has been confirmed and is being processed.
                </p>

                <div className="space-y-2 rounded-xl bg-slate-200/70 p-4">
                  <p className="text-sm text-slate-700">
                    Phone: <span className="text-sky-400 font-bold">{phone}</span>
                  </p>
                  <p className="text-sm text-slate-700">
                    Package: <span className="text-sky-400 font-bold">{selectedPkg?.id}</span>
                  </p>
                  <p className="text-sm text-slate-700">
                    Network: <span className="text-sky-400 font-bold">{finalNetwork}</span>
                  </p>
                  <p className="text-sm text-slate-700">
                    Amount: <span className="text-green-400 font-bold">{formatGhs(getPromoPrice(selectedPkg?.price, effectivePromoActive, promoDiscountPercent))}</span>
                  </p>
                  <p className="mt-2 text-xs text-slate-500">
                    Ref: {successRef}
                  </p>
                </div>

                <p className="text-xs text-yellow-400 font-semibold">
                  ⏱️ Delivery in 5-10 minutes
                </p>
              </div>

              {/* BUTTONS */}
              <div className="space-y-3">
                <button
                  onClick={buyAnother}
                  className="w-full bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 rounded-xl transition"
                >
                  Buy Another Package
                </button>

                <button
                  onClick={() => setShowSuccessModal(false)}
                  className="w-full rounded-xl border border-slate-400 py-3 font-bold text-slate-800 transition hover:border-slate-500"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}